import { prepareColorTexture } from "./bustColor";

/** Keep the visible rest image as the sole initial request, then fill a bounded queue. */
export function createPortraitLoader({ manifest, assetRoot, restImage, reduced, onTexture, onRestError }) {
  const doc = restImage.ownerDocument, win = doc.defaultView;
  const frames = manifest.frames;
  const restIndex = frames.findIndex(frame => frame.yaw === -15);
  const prioritized = frames.map((_, index) => index).filter(index => index !== restIndex)
    .sort((a, b) => Math.abs(frames[a].yaw - frames[restIndex].yaw) -
      Math.abs(frames[b].yaw - frames[restIndex].yaw));
  const completed = new Set();
  const active = new Map();
  let queue = [], destroyed = false, restReady = false;

  async function texture(image, isCurrent) {
    await image.decode();
    if (!isCurrent()) return null;
    if (image.naturalWidth !== manifest.width || image.naturalHeight !== manifest.height) {
      throw new Error("Incorrect portrait dimensions");
    }
    return image;
  }

  async function request(index) {
    const image = new win.Image();
    active.set(index, image);
    image.decoding = "async";
    image.src = assetRoot + "/" + frames[index].file;
    const isCurrent = () => !destroyed && !reduced && active.get(index) === image;
    try {
      const decoded = await texture(image, isCurrent);
      if (decoded && isCurrent()) {
        completed.add(index);
        onTexture(index, prepareColorTexture(doc, decoded, frames[index].color_grade));
      }
    } catch {
      // A missing pose stays unavailable; the closest successful pose remains usable.
      if (isCurrent()) completed.add(index);
    } finally {
      if (active.get(index) === image) { active.delete(index); pump(); }
    }
  }

  function pump() {
    if (destroyed || reduced || !restReady) return;
    while (active.size < 10 && queue.length) request(queue.shift());
  }

  function loadMore({ idle = false } = {}) {
    if (destroyed || reduced) return;
    // Fill out the starting silhouette before distant poses, regardless of pointer position.
    queue = (idle ? prioritized.slice(0, 3) : prioritized).filter(index =>
      !completed.has(index) && !active.has(index));
    pump();
  }

  function cancelAdditional() {
    queue = [];
    // Removing src asks the browser to abandon owned requests. Stale decode promises
    // also lose their active entry, so they cannot calibrate, publish, or drain a queue.
    active.forEach(image => image.removeAttribute("src"));
    active.clear();
  }

  async function loadRest() {
    try {
      const image = await texture(restImage, () => !destroyed);
      if (!image || destroyed) return;
      completed.add(restIndex);
      restReady = true;
      onTexture(restIndex, prepareColorTexture(doc, image, frames[restIndex].color_grade));
      pump();
    } catch (error) {
      if (!destroyed) onRestError(error);
    }
  }
  loadRest();

  return {
    loadMore,
    setReduced(value) { reduced = value; if (reduced) cancelAdditional(); },
    destroy() { destroyed = true; cancelAdditional(); },
  };
}
